import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPreview } from './client';
import { subscribeScreenEvents } from './live';

/**
 * Loads data now, every `intervalMs`, and within ~300 ms of any screen event on the live
 * stream (so killing an emulator shows up without a refresh).
 */
export function useLive<T>(load: () => Promise<T | undefined>, deps: unknown[], intervalMs = 10_000) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState(false);
  const [live, setLive] = useState(false);
  const loadRef = useRef(load);
  loadRef.current = load;

  const refresh = useCallback(async () => {
    try {
      const value = await loadRef.current();
      if (value === undefined) setError(true);
      else {
        setData(value);
        setError(false);
      }
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    setData(null);
    void refresh();
    const timer = setInterval(refresh, intervalMs);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    let pending: ReturnType<typeof setTimeout> | null = null;
    const stop = subscribeScreenEvents(
      (e) => {
        // Heartbeats arrive every few seconds per screen; state changes matter most.
        const delay = e.reason === 'heartbeat' ? 2000 : 300;
        if (!pending) pending = setTimeout(() => ((pending = null), void refresh()), delay);
      },
      (connected) => setLive(connected),
    );
    return () => {
      stop();
      if (pending) clearTimeout(pending);
    };
  }, [refresh]);

  return { data, error, live, refresh };
}

export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Latest preview frame, refetched whenever the player uploads a new one. */
export function usePreview(screenId: string, capturedAt: string | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!capturedAt) return;
    let cancelled = false;
    fetchPreview(screenId).then((next) => {
      if (!cancelled && next) setUrl(next);
    });
    return () => {
      cancelled = true;
    };
  }, [screenId, capturedAt]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  return url;
}

/** Loads once (and again on `reload`), for admin pages that do not need live updates. */
export function useLoad<T>(load: () => Promise<T | undefined>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState(false);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async () => {
    try {
      const value = await loadRef.current();
      if (value === undefined) setError(true);
      else {
        setData(value);
        setError(false);
      }
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, reload };
}
